import { Button, Container, IconButton, Typography } from "@material-ui/core"
import {
  getGroupPurchaseGetGroupPurchaseSettings,
  getPaymentPaymentMethods,
  postGroupPurchaseGroupPurchaseMaxPrice,
  postPaymentGetGroupPurchaseBit,
  postPaymentPaymentDoneBit,
} from "api"
import { useIsDesktop } from "common/MediaQueries"
import { formatCurrencyWithSybmol } from "common/utils/formatters"
import { GenericInput } from "components/GenericInput/GenericInput"
import PencilIcon from "components/Icons/PencilIcon"
import {
  GroupPurchasesSteps,
  useGroupPurchasesContext,
} from "components/PurchasePage/GroupPurchasesContext"
import { usePaymentContext } from "components/PurchasePage/Preview/Payment/PaymentContext"
import {
  bitPay,
  loadBitCom,
  saveGroupPurchasePaymentDataToLocalStorage,
} from "components/PurchasePage/Preview/Payment/bitHelpers"
import { usePurchaseContext } from "components/PurchasePage/PurchaseContext"
import FormikForm from "components/Reusable/Bit/Form/FormikForm"
import { invalid, regex, required } from "components/Reusable/Bit/FormikSchema"
import LoaderPopup from "components/Reusable/LoaderPopup"
import { useRouter } from "next/router"
import { useEffect, useMemo, useRef, useState } from "react"
import {
  isAndroid,
  isChrome,
  isEdge,
  isFirefox,
  isIOS,
  isOpera,
  isSafari,
} from "react-device-detect"
import styled from "styled-components"
import colors from "theme/colors"
import * as yup from "yup"
import { PaymentSteps } from "~/pages/groupPurchases/payment"
import {
  StyledFieldWrapper,
  StyledInputLabelBottom,
} from "./GroupPurchasesAmountPage"
import GroupPurchasesHeader from "./GroupPurchasesHeader"
import GroupPurchasesPageCover from "./GroupPurchasesPageCover"
import { StyledMain } from "./LeaderDetailsPage"

const StyledWrapper = styled(Container)`
  margin-top: 50px;
  padding-right: 0px;
  padding-left: 0px;

  ${(p) => p.theme.breakpoints.down("sm")} {
    padding-top: 30px;
  }

  ${(p) => p.theme.breakpoints.up("md")} {
    display: flex;
    gap: 15%;
    flex-direction: row-reverse;
    margin-top: 120px;
    justify-content: space-between;
    margin-bottom: 100px;
  }
`

const StyledPaymentChoiceContainer = styled.div`
  margin-bottom: -30px;
  ${(p) => p.theme.breakpoints.down("sm")} {
    margin-bottom: 90px;
  }
`

const StyledPayments = styled.div<{ $isDesktop: boolean }>`
  display: flex;
  gap: 25px;

  flex-direction: column;
  ${(p) => !p.$isDesktop && "justify-content: space-between;"}
`

const StyledPayWithIcon = styled.div`
  line-height: 20px;
  direction: ltr;
  display: flex;
  align-items: center;
  font-size: 20px;
  font-family: Speedee, NarkisBlock, sans-serif;
  color: #000000;
`

const StyledButtonText = styled(Typography).attrs({ variant: "body1" })<{
  $bit?: boolean
}>`
  text-align: right;
  font-family: ${(p) => (p.$bit ? "poalimBIT-Medium" : "NarkisBlock")};
  font-size: ${(p) => (p.$bit ? "20px" : "24px")};
  font-weight: ${(p) => (p.$bit ? "500" : "bold")};
  font-stretch: condensed;
  font-style: normal;
  color: ${(p) => (p.$bit ? p.theme.colors.white : p.theme.colors.black)};
`

const StyledDivider = styled.div`
  border-right: 1px solid ${({ theme }) => theme.colors.darkGreen};
  width: 1px;
  height: 25px;
  margin-left: 10px;
  margin-right: 10px;
`

const StyledImg = styled.img<{ $bit?: boolean; $marginLeft?: string }>`
  margin-right: ${(p) => (p.$bit ? "15px" : "5px")};
  margin-left: ${(p) => (p.$marginLeft ? p.$marginLeft : "5px")};
`

const StyledPaymentTitle = styled(Typography).attrs({ variant: "h2" })`
  text-align: right;
  font-family: Speedee, NarkisBlock;
  font-size: 40px;
  font-weight: bold;
  font-stretch: condensed;
  font-style: normal;
  line-height: 46px;
  letter-spacing: normal;
`

const StyledPaymentButton = styled(Button).attrs({ variant: "outlined" })<{
  $isBit?: boolean
}>`
  width: 328px;
  height: 56px;

  ${(p) => p.theme.breakpoints.down("sm")} {
    width: 100%;
  }

  border: ${(p) => (p.$isBit ? "none" : "2px solid var(--Yellow, #FFBC0D);")};
  background: ${(p) =>
    p.$isBit
      ? "linear-gradient(180deg, #2B5586 0%, #3B96B1 100%)"
      : p.theme.colors.white};
  color: ${({ theme, $isBit }) =>
    $isBit ? theme.colors.white : theme.colors.black};
  border-radius: 8px;

  font-size: 24px;
  font-weight: 500;
  text-transform: none;

  &:hover {
    border-radius: 10.125px;
    border: 2.25px solid var(--Yellow, #ffbc0d);
    background: var(--White, #fff);
    box-shadow: 0px 4.5px 9px 0px rgba(45, 45, 45, 0.16);
    font-size: 26px;
  }

  &:focus {
    border-radius: 9px;
    border: 2px solid var(--Yellow, #ffbc0d);
    background: var(--White, #fff);
    box-shadow: 0px 4px 8px 0px rgba(45, 45, 45, 0.16);
  }

  &:active {
    border-radius: 8px;
    border: 2px solid var(--Yellow, #ffbc0d);
    background: rgba(247, 247, 247, 0.5);
  }
`

const NoPaymentWrapper = styled.div`
  text-align: right;
`

const NoPaymentSubtitle = styled(Typography).attrs({ variant: "body1" })`
  font-size: 24px;
  line-height: 30px;
  font-weight: 400;
  text-align: right;
`

const StyledTryAgainButton = styled(Button).attrs({ variant: "contained" })`
  width: 158px;
  height: 48px;
  background-color: ${colors.darkYellow};
  border: none;
  color: ${colors.black};
  font-size: 24px;
  border-radius: 0px;
  line-height: 30px;
  word-break: break-word;
  margin-top: 25px;
  ${(p) => p.theme.breakpoints.down("sm")} {
    width: 100%;
    font-size: 24px;
  }
`

type PaymentType = "apple/google" | "creditCard"

const PaymentIframeGroupPurchasesPage = () => {
  const amountInputRef = useRef(null)
  const isDesktop = useIsDesktop()
  const [loading, setLoading] = useState(false)
  const [availablePayment, setAvailablePayment] = useState(null)

  const {
    groupPurchaseData,
    orderId,
    groupPurchaseUserGuid,
    setPaymentInitiationId,
    setTransactionSerialId,
    setGroupPurchaseData,
  } = usePurchaseContext()
  const { setPaymentSteps } = usePaymentContext()
  const { groupPurchasesData, setGroupPurchasesSteps, isAdmin } =
    useGroupPurchasesContext()

  const router = useRouter()
  useEffect(() => {
    if (router?.query?.page !== "groupPurchases") {
      if (!groupPurchaseData) router.push("/")
    }
  }, [])

  const [price, setPrice] = useState(groupPurchaseData?.price)

  useEffect(() => {
    groupPurchasesData && setGroupPurchaseData(groupPurchasesData)
  }, [groupPurchasesData])
  useEffect(() => {
    setGroupPurchaseData({ ...groupPurchaseData, price: price })
  }, [price])

  useEffect(() => {
    const getPaymentTypes = async () => {
      const { data } = await getPaymentPaymentMethods(
        null,
        isChrome
          ? "chrome"
          : isSafari
          ? "safari"
          : isFirefox
          ? "firefox"
          : isOpera
          ? "opera"
          : isEdge
          ? "edge"
          : "other browser",
        groupPurchaseUserGuid
      )
      setAvailablePayment(data.body)
    }
    getPaymentTypes()
  }, [])

  const allValuesFalse = useMemo(
    () =>
      availablePayment &&
      Object.values(availablePayment).every((value) => value === false),
    [availablePayment]
  )

  /**
   * This function will be called when a user selects a payment option
   *
   *
   */
  useEffect(() => {
    const storedData = localStorage.getItem(groupPurchaseUserGuid)
    if (storedData) {
      const { expirationTime } = JSON.parse(storedData)
      const currentTime = new Date().getTime()

      if (currentTime > expirationTime) {
        localStorage.removeItem(groupPurchaseUserGuid)
      }
    }
  }, [])

  const handlePaymentSelection = (paymentOption: PaymentType) => {
    if (!paymentOption) return
    if (paymentOption === "creditCard") {
      router.push("/groupPurchases/payment/creditCard")
    }
    if (paymentOption === "apple/google") {
      router.push("/groupPurchases/payment/wallet")
    }
  }

  /**
   * Handling bit payment
   *
   *
   */

  const payBit = (transactionSerialId: string, paymentInitiationId: string) => {
    bitPay({
      transactionSerialId,
      paymentInitiationId,
      onApproval: async () => {
        try {
          setLoading(true)
          await postPaymentPaymentDoneBit({
            paymentInitiationId: paymentInitiationId,
          })
          setLoading(false)
          setPaymentSteps(PaymentSteps.SUCCESS)
        } catch (error) {
          setLoading(false)
          setPaymentSteps(PaymentSteps.FAILURE)
          console.log(error)
        }
      },
      onCancel: async () => {
        try {
          setLoading(true)

          await postPaymentPaymentDoneBit({
            paymentInitiationId: paymentInitiationId,
          })

          setLoading(false)
        } catch (error) {
          setLoading(false)
          console.log(error)
        }
      },
    })
  }

  const getBitPayment = async () => {
    try {
      setLoading(true)
      const { data } = await postPaymentGetGroupPurchaseBit({
        groupPurchaseUserGuid: groupPurchaseUserGuid,
        amount: groupPurchaseData.price,
        browser: isChrome
          ? "chrome"
          : isSafari
          ? "safari"
          : isFirefox
          ? "firefox"
          : isOpera
          ? "opera"
          : isEdge
          ? "edge"
          : "other browser",
      })
      setLoading(false)
      if (!isDesktop) {
        saveGroupPurchasePaymentDataToLocalStorage(groupPurchaseUserGuid, {
          paymentData: data.body.bitResponse,
          groupPurchaseData: groupPurchaseData,
          orderId: orderId,
          groupPurchaseUserGuid: groupPurchaseUserGuid,
          giftCardPrice: groupPurchaseData.price,
        })
      }
      setTransactionSerialId(data.body.bitResponse.transactionSerialId)
      setPaymentInitiationId(data.body.bitResponse.paymentInitiationId)

      loadBitCom({
        isDesktop,
        groupPurchaseUserGuid,
        paymentInitiationId: data.body.bitResponse.paymentInitiationId,
        transactionSerialId: data.body.bitResponse.transactionSerialId,
        isAndroid,
        isIOS,
        pay: payBit,
      })
    } catch (error) {
      setLoading(false)
      console.log(error)
    }
  }

  const groupPurchasesInitialData = {
    price: price || "",
  }
  const priceRef = useRef(null)
  const [maxAmount, setMaxAmount] = useState(0)
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getGroupPurchaseGetGroupPurchaseSettings()
        const maxAmountPriceResponse =
          await postGroupPurchaseGroupPurchaseMaxPrice({
            //@ts-ignore
            groupPurchaseGuid: groupPurchaseData.guid,
          })
        if (!isAdmin) {
          setMaxAmount(maxAmountPriceResponse?.data?.body)
        } else {
          setMaxAmount(response?.data?.body?.maxPrice)
        }
        if (response && groupPurchasesData.price) {
          if (priceRef.current) {
            setTimeout(() => {
              priceRef.current?.getElementsByTagName("input")[0].focus()
              setTimeout(() => {
                priceRef.current?.getElementsByTagName("input")[0].blur()
              }, 50)
            }, 50)
          }
        }
      } catch (error) {
        console.error(error)
      }
    }
    fetchData()
  }, [])

  const schemaGroupPurchasesAmount = yup
    .object({
      price: yup
        .string()
        .matches(regex.amount, invalid.amount)
        .test("min-value", required.default, (value) => {
          const numericValue = parseFloat(value?.replace(/[^\d.-]/g, ""))
          return numericValue > 0
        })
        .test("max-value", invalid.groupPurchaseAmount, (value) => {
          const numericValue = parseFloat(value?.replace(/[^\d.-]/g, ""))
          return numericValue <= maxAmount
        })
        .required(required.default),
    })
    .defined()

  const goBack = () => {
    setGroupPurchasesSteps(GroupPurchasesSteps.LeaderDetails)
    router.push("/groupPurchases/otp")
  }

  return (
    <StyledWrapper>
      <GroupPurchasesPageCover backBtnOnClick={goBack} />
      <StyledMain>
        <GroupPurchasesHeader
          mainTitle={
            isAdmin
              ? "עכשיו זה הזמן להשלים את החלק שלך במתנה"
              : //@ts-ignore
                `היי הוזמנת לאיסוף כסף עבור רכישה קבוצתית ל "${groupPurchaseData?.groupName}" `
          }
          subTitle={
            isAdmin ? (
              `תשלום ראשוני נדרש ממך כדי לייצר קבוצה עם סכום התחלתי. אח"כ נשלח קישור לשיתוף עם שאר המשתתפים.`
            ) : (
              <>
                {`התשלום יתבצע רק לאחר השלמת ההזמנה ע"י מנהל/ת הקבוצה. `}
                <br />
                למשלמים בכרטיס דיירקט החיוב יהיה מיידי.
              </>
            )
          }
          component="h2"
          subtitleComponent="body2"
          backBtnOnClick={goBack}
        />
        <FormikForm
          validationSchema={schemaGroupPurchasesAmount}
          initialValues={groupPurchasesInitialData}
          onSubmit={async (values) => {
            setGroupPurchaseData({
              ...groupPurchaseData,
              price:
                values.price && typeof values.price === "string"
                  ? parseInt(
                      values.price.split("₪")?.[
                        values.price.split("₪")?.length - 1
                      ]
                    )
                  : values.price,
            })
          }}
          render={(formik) => (
            <>
              <StyledFieldWrapper>
                <GenericInput
                  innerRef={priceRef}
                  name="price"
                  label="הזנת סכום"
                  type="text"
                  maxLength={30}
                  $width="328"
                  $mobileWidth="100%"
                  topMargin="0"
                  placeholder="סכום לאסוף מכל משתתף/ת"
                  numeric={true}
                  inputRef={amountInputRef}
                  $hasIcon={true}
                  InputProps={{
                    startAdornment: (
                      <IconButton
                        onClick={() => {
                          if (amountInputRef) {
                            amountInputRef.current?.focus()
                          }
                        }}
                        aria-label="עריכת הסכום"
                      >
                        <PencilIcon />
                      </IconButton>
                    ),
                  }}
                  value={formatCurrencyWithSybmol(price || 0)}
                  onChange={(e) => {
                    setPrice(
                      typeof e.target.value === "string"
                        ? parseInt(
                            e.target.value.split("₪")?.[
                              e.target.value.split("₪")?.length - 1
                            ]
                          )
                        : e.target.value
                    )
                  }}
                />
              </StyledFieldWrapper>
              {!isAdmin && (
                <StyledInputLabelBottom>
                  ניתן לרכוש עד לסכום של {maxAmount} ש”ח
                </StyledInputLabelBottom>
              )}
              <StyledPaymentChoiceContainer>
                {!allValuesFalse ? (
                  <>
                    <StyledPayments $isDesktop={isDesktop}>
                      {availablePayment?.creditCard && (
                        <StyledPaymentButton
                          type="submit"
                          onClick={() =>
                            ((formik.initialValues && formik.isValid) ||
                              (formik.isValid &&
                                Object.keys(formik.touched).length > 0)) &&
                            handlePaymentSelection("creditCard")
                          }
                          aria-label="תשלום באמצעות כרטיס אשראי"
                        >
                          <StyledPayWithIcon>
                            <StyledButtonText>כרטיס אשראי</StyledButtonText>
                            <StyledImg
                              src="/img/icons/CreditCardLogo.svg"
                              alt=""
                            />
                          </StyledPayWithIcon>
                        </StyledPaymentButton>
                      )}
                      {availablePayment?.wallet && (
                        <StyledPaymentButton
                          type="submit"
                          onClick={() =>
                            ((formik.initialValues && formik.isValid) ||
                              (formik.isValid &&
                                Object.keys(formik.touched).length > 0)) &&
                            handlePaymentSelection("apple/google")
                          }
                          aria-label="תשלום באמצעות ארנק דיגיטלי גוגל פיי או אפל פיי"
                        >
                          <>
                            <StyledPayWithIcon>
                              <StyledImg
                                src="/img/icons/GooglePayIcon.svg"
                                alt=""
                                $marginLeft={"0px"}
                              />
                              Pay
                            </StyledPayWithIcon>
                            <StyledDivider />
                            <StyledPayWithIcon>
                              <StyledImg
                                src="/img/icons/ApplePayIcon.svg"
                                alt=""
                              />
                              Pay
                            </StyledPayWithIcon>
                          </>
                        </StyledPaymentButton>
                      )}
                      {availablePayment?.bit && (
                        <StyledPaymentButton
                          $isBit
                          type="submit"
                          onClick={() =>
                            ((formik.initialValues && formik.isValid) ||
                              (formik.isValid &&
                                Object.keys(formik.touched).length > 0)) &&
                            getBitPayment()
                          }
                          aria-label="תשלום באמצעות ביט"
                        >
                          <StyledPayWithIcon>
                            <StyledImg
                              src="/img/icons/bitLogo.svg"
                              alt=""
                              $bit={true}
                            />
                            <StyledButtonText $bit={true}>
                              {" "}
                              תשלום באמצעות
                            </StyledButtonText>
                          </StyledPayWithIcon>
                        </StyledPaymentButton>
                      )}
                    </StyledPayments>
                  </>
                ) : (
                  <NoPaymentWrapper>
                    <StyledPaymentTitle>
                      אופסי, אי אפשר לבצע רכישה כרגע באתר
                    </StyledPaymentTitle>
                    <NoPaymentSubtitle>
                      אנחנו על זה! בבקשה לנסות שוב בקרוב
                    </NoPaymentSubtitle>
                    <StyledTryAgainButton
                      onClick={() => router.push("/groupPurchases")}
                    >
                      בואו ננסה שוב
                    </StyledTryAgainButton>
                  </NoPaymentWrapper>
                )}
              </StyledPaymentChoiceContainer>
            </>
          )}
        ></FormikForm>
        <LoaderPopup isOpen={loading} />
      </StyledMain>
    </StyledWrapper>
  )
}

export default PaymentIframeGroupPurchasesPage
